import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Container,
  Spinner,
  Card,
  Badge,
  Row,
  Col,
} from "react-bootstrap";
import { FaCheckCircle, FaTimesCircle, FaQuestionCircle } from "react-icons/fa";
import { useStudentResponseDissertatives } from "../../../../hooks/studentResponseDissertatives/useStudentResponseDissertatives";
import { useSanitizeInput } from "../../../../constants/configJodit";
import {
  mapDifficultyLevel,
  mapEducationLevel,
} from "../../../../utils/questions/questionsMap";
import { STORAGE_URL } from "../../../../constants/consts";

type ModalViewResponseProps = {
  responseId: string;
  token: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdated?: () => void;
};

export const ModalViewResponse: React.FC<ModalViewResponseProps> = ({
  responseId,
  token,
  isOpen,
  onClose,
  onUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState<any>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const { sanitizeInput } = useSanitizeInput();
  const { getStudentResponsesById, updateStudentResponseDissertative } =
    useStudentResponseDissertatives(token);

  // estado local para seleção de status
  const [newStatus, setNewStatus] = useState<
    "correct" | "incorrect" | "undefinedByReviewer"
  >("undefinedByReviewer");

  // carrega dados e define newStatus a partir de isWrong
  useEffect(() => {
    if (!responseId || !isOpen) return;
    setLoading(true);
    setError("");
    getStudentResponsesById(responseId)
      .then((resp) => {
        setResponseData(resp);
        if (resp.isWrong === true) setNewStatus("correct");
        else if (resp.isWrong === false) setNewStatus("incorrect");
        else setNewStatus("undefinedByReviewer");
      })
      .catch(() => setError("Falha ao carregar os dados da resposta"))
      .finally(() => setLoading(false));
  }, [responseId, isOpen, getStudentResponsesById]);

  // mensagem de status exibida
  const getStatusMessage = (r: any) => {
    if (r.isWrong === null || r.status?.undefinedByReviewer)
      return "Não Marcada";
    if (r.isWrong === true || r.status?.correct) return "Correta";
    if (r.isWrong === false || r.status?.incorrect) return "Incorreta";
    return "Status desconhecido";
  };

  const getStatusColor = (r: any) => {
    if (r.isWrong === null || r.status?.undefinedByReviewer) return "warning";
    if (r.isWrong === true || r.status?.correct) return "success";
    if (r.isWrong === false || r.status?.incorrect) return "danger";
    return "secondary";
  };

  const getAnswerImageUrl = (file: string) =>
    `${STORAGE_URL}/${file.replace(/^public\//, "")}`;

  // salva a alteração de status invertendo no is_wrong
  const handleSave = async () => {
    if (!responseData) return;
    setSaving(true);
    const isWrong =
      newStatus === "correct" ? true : newStatus === "incorrect" ? false : null;
    try {
      await updateStudentResponseDissertative({
        id: responseData.id,
        is_wrong: isWrong,
      });
      onUpdated?.();
      onClose();
    } catch {
      setError("Falha ao salvar alteração");
    } finally {
      setSaving(false);
    }
  };

  // renderiza um card de opção de status
  const renderStatusCard = (
    status: "correct" | "incorrect" | "undefinedByReviewer",
    Icon: React.ComponentType<{ size: string; className: string }>,
    label: string,
    color: string
  ) => {
    const isSelected = newStatus === status;
    return (
      <Col xs={12} md={4}>
        <Card
          className={`mb-3 status-card ${isSelected ? "selected" : ""}`}
          border={isSelected ? color : "light"}
          onClick={() => setNewStatus(status)}
          style={{
            cursor: "pointer",
            transition: "all 0.3s ease",
            transform: isSelected ? "translateY(-5px)" : undefined,
            boxShadow: isSelected
              ? `0 5px 15px rgba(${
                  color === "success"
                    ? "40, 167, 69"
                    : color === "danger"
                    ? "220, 53, 69"
                    : "255, 193, 7"
                }, 0.4)`
              : undefined,
          }}
        >
          <Card.Body className="text-center p-4">
            <div className="mb-3">
              <Icon size="2em" className={`text-${color}`} />
            </div>
            <h5 className="mb-0">{label}</h5>
            {isSelected && <Badge bg="primary">Selecionado</Badge>}
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size="xl"
      centered
      dialogClassName="modal-dialog-centered modal-xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>Visualizar Resposta Dissertativa</Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{ maxHeight: "80vh", overflowY: "auto", padding: "1rem" }}
      >
        <Container fluid>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Carregando...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : responseData ? (
            <>
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-light">
                  <h5 className="mb-0">Detalhes da Questão</h5>
                </Card.Header>
                <Card.Body>
                  <Badge bg={getStatusColor(responseData)} className="mb-3">
                    {getStatusMessage(responseData)}
                  </Badge>

                  <div className="mb-3">
                    <h6 className="fw-bold">Número da Questão</h6>
                    <p className="fs-5 mb-0">
                      Página {responseData.page} | Questão{" "}
                      {responseData.questionCount}
                    </p>
                  </div>

                  <p className="mb-1">
                    <strong>Nível de Educação:</strong>{" "}
                    {mapEducationLevel(responseData.question.educationLevel)}
                  </p>
                  <p className="mb-1">
                    <strong>Dificuldade:</strong>{" "}
                    {mapDifficultyLevel(responseData.question.difficultyLevel)}
                  </p>
                  {responseData.student && (
                    <p className="mb-1">
                      <strong>Aluno:</strong> {responseData.student.name}
                    </p>
                  )}

                  <h6 className="mt-4">Enunciado:</h6>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    <div
                      className="p-3 bg-light rounded"
                      dangerouslySetInnerHTML={{
                        __html:
                          sanitizeInput(
                            responseData.subquestions[0].statement
                          ) || "Nenhum enunciado disponível.",
                      }}
                    />
                  </div>

                  <h6 className="mt-4">Justificativa da Questão:</h6>
                  <div
                    style={{ maxHeight: "150px", overflowY: "auto" }}
                    className="p-3 bg-light rounded text-start"
                    dangerouslySetInnerHTML={{
                      __html:
                        sanitizeInput(
                          responseData.subquestions[0].answerJustification
                        ) || "Sem justificativa disponível.",
                    }}
                  />

                  <div className="mt-5 pt-2">
                    <h5 className="mb-3 border-bottom pb-2">
                      Status da Correção
                    </h5>
                    <Row className="justify-content-center">
                      {renderStatusCard(
                        "correct",
                        FaCheckCircle,
                        "Correta",
                        "success"
                      )}
                      {renderStatusCard(
                        "incorrect",
                        FaTimesCircle,
                        "Incorreta",
                        "danger"
                      )}
                      {renderStatusCard(
                        "undefinedByReviewer",
                        FaQuestionCircle,
                        "Não Marcada",
                        "warning"
                      )}
                    </Row>
                  </div>
                </Card.Body>
              </Card>

              <Card className="shadow-sm mb-4">
                <Card.Header className="bg-light">
                  <h5 className="mb-0">Resposta do Aluno</h5>
                </Card.Header>
                <Card.Body>
                  {responseData.answerAreaFile ? (
                    <div className="text-center">
                      <a
                        href={getAnswerImageUrl(responseData.answerAreaFile)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={getAnswerImageUrl(responseData.answerAreaFile)}
                          alt="Resposta do aluno"
                          className="img-fluid rounded shadow"
                          style={{ cursor: "pointer" }}
                        />
                      </a>
                      <p className="mt-2 text-muted">Clique para ampliar</p>
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      <FaQuestionCircle size="3em" />
                      <p>Imagem não disponível</p>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer className="d-flex justify-content-end">
                  <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={saving}
                    className="me-2"
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      "Salvar"
                    )}
                  </Button>
                </Card.Footer>
              </Card>
            </>
          ) : null}
        </Container>
      </Modal.Body>
    </Modal>
  );
};
